import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  Undo2,
  Check,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Lock,
} from "lucide-react";

/**
 * ActionGuide - Tooltip window explaining action buttons and indicators.
 * Placed below To Do cards on desktop; rendered as a mobile floating pill via
 * a wrapper in AppPage. Can be minimized but not completely dismissed.
 *
 * expandUpward - When true (mobile floating placement), the header sits at
 * the bottom of the card and the content grows upward when expanded. This
 * keeps the tap target anchored and prevents the expanded panel from covering
 * the columns' inline "Add a task" forms sitting below.
 */
export const ActionGuide = ({ autoCollapsed = false, expandUpward = false, forceCollapsed = false }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [userToggled, setUserToggled] = useState(false);

  // Auto-collapse when parent signals, but only if user hasn't manually toggled
  useEffect(() => {
    if (autoCollapsed && !userToggled) {
      setIsExpanded(false);
    }
  }, [autoCollapsed, userToggled]);

  // forceCollapsed is a stronger signal (e.g. an "Add a task" form is open) —
  // it collapses the panel regardless of userToggled so the guide gets out
  // of the way. We do NOT reset userToggled so once forceCollapsed goes away,
  // the user's last explicit state (collapsed) still stands and they can
  // re-expand manually.
  useEffect(() => {
    if (forceCollapsed) {
      setIsExpanded(false);
    }
  }, [forceCollapsed]);

  const header = (
    <button
      onClick={() => {
        setUserToggled(true);
        setIsExpanded(!isExpanded);
      }}
      className={`w-full flex items-center justify-between px-4 py-2 bg-[#FDFBF7] hover:bg-[#F5F3EF] transition-colors pointer-events-auto ${
        expandUpward ? "border-t border-stone-100" : "border-b border-stone-100"
      }`}
      aria-label={isExpanded ? "Minimize guide" : "Expand guide"}
    >
      <span className="text-[13.5px] font-semibold text-[#27272A]">Card Action Guide</span>
      <div className="p-1 text-stone-500">
        {isExpanded
          ? (expandUpward ? <ChevronDown size={14} /> : <ChevronUp size={14} />)
          : (expandUpward ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
      </div>
    </button>
  );

  const body = (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`overflow-hidden ${expandUpward ? "pointer-events-none" : ""}`}
        >
          <div className="p-3 space-y-[7.6px]">
            {/* Card Actions */}
            <div className="space-y-[5.7px]">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <span className="text-stone-400/70 text-2xl font-bold leading-none">*</span>
                </div>
                {/* Highlighter effect on text - matches the task card highlighter */}
                <span
                  className="text-[13.5px] text-[#1A1A1C] relative"
                  style={{
                    background:
                      "linear-gradient(to bottom, transparent 8%, rgba(253, 224, 71, 0.28) 15%, rgba(253, 224, 71, 0.38) 50%, rgba(253, 224, 71, 0.32) 85%, transparent 92%)",
                    padding: "0.05em 0.3em",
                    margin: "0 -0.3em",
                    borderRadius: "3px",
                  }}
                >
                  Important
                </span>
              </div>
              {/* Nudge row removed while email nudges are being finalized —
                  see /app/frontend/src/config/featureFlags.js */}

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <GripVertical size={14} className="text-stone-500" />
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">Touch & drag to move</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Lock size={12} className="text-stone-600" />
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">Add task to Lock Screen</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Play size={12} className="text-stone-600" />
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">Push to Doing</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                  <Check size={12} className="text-[#4CAF50]" />
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">Mark as Done</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <Undo2 size={12} className="text-stone-600" />
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">Return to To Do</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-stone-500 text-[10px]">Aa</span>
                </div>
                <span className="text-[13.5px] text-[#1A1A1C]">TAP text to EDIT</span>
              </div>
            </div>

            {/* Tip */}
            <div className="pt-[5.7px] border-t border-stone-100">
              <p className="text-[13.5px] text-[#52525B] italic flex items-center gap-1 flex-wrap">
                Click or drag
                <span className="inline-flex items-center justify-center not-italic">
                  <span className="grid grid-cols-2 gap-[1.65px]">
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                    <span className="w-[2.2px] h-[2.2px] rounded-full bg-stone-500" />
                  </span>
                </span>
                tasks between columns
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: expandUpward ? -10 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`mt-6 bg-white rounded-xl overflow-hidden scale-95 ${
        expandUpward ? "origin-bottom pointer-events-none" : "origin-top"
      }`}
      style={{
        boxShadow:
          "0 2px 6px rgba(0, 0, 0, 0.07), 0 8px 20px rgba(0, 0, 0, 0.09), 0 16px 40px rgba(0, 0, 0, 0.07)",
      }}
    >
      {expandUpward ? (
        <>
          {body}
          {header}
        </>
      ) : (
        <>
          {header}
          {body}
        </>
      )}
    </motion.div>
  );
};

export default ActionGuide;
